import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { addData, deleteData, loadData, updateData } from "../../../../config/data";
import EmptyAsset from "../emptyAsset";
import Lang from "../../../../config/lang";
import { useProfileTab } from "../../ProfileTabContext";

const OrganizationForm = ({ setIsFading, updatePercentage, profileCompletion }) => {
  const { lang } = useSelector((state) => state.globalReducer);
  const applicantID = localStorage?.getItem("applicantID");
  const userToken = localStorage?.getItem('authToken') || '';
  const { currentTabIndex, setCurrentTabIndex } = useProfileTab();

  const [isEdit, setisEdit] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setisLoading] = useState('');

  // Load organization data
  const loadOrgData = async () => {
    loadData({
      url: `OrganizationalHistory`,
      params: [{ title: "filter", value: `applicant:${applicantID}` }],
      userToken,
    }).then((res) => {
      if (res?.data) {
        setOrganizations(res.data);
      }
    });
  };

  useEffect(() => {
    loadOrgData();
  }, []);


  const isFormReady = () => {
    const hasEmptyId = organizations.some(item => !item.organizationName || !item.position || !item.year || !item.locationCity);
    return !hasEmptyId;
  }

  // Handle change
  const handleChange = (index, field, value) => {
    const updated = [...organizations];
    updated[index][field] = value;
    setOrganizations(updated);
  };

  // {Lang(lang,'add_org')}
  const handleAdd = () => {
    setisEdit(true)
    setOrganizations([
      ...organizations,
      {
        organizationHistoryID: null,
        applicantID: applicantID,
        organizationName: "",
        activityType: "",
        position: "",
        year: "",
        locationCity: "",
      },
    ]);
  };

  // Save single organization
  const saveOrganization = async (org) => {
    try {
      if (org.organizationHistoryID) {
        await updateData({
          url: "OrganizationalHistory",
          userToken,
          reqBody: {
            organizationHistoryID: org.organizationHistoryID || undefined,
            applicantID: applicantID,
            organizationName: org.organizationName,
            activityType: org.activityType,
            position: org.position,
            year: parseInt(org.year) || 0,
            locationCity: org.locationCity,
          },
        });
      } else {
        await addData({
          url: "OrganizationalHistory",
          userToken,
          reqBody: {
            applicantID: applicantID,
            organizationName: org.organizationName,
            activityType: org.activityType,
            position: org.position,
            year: parseInt(org.year) || 0,
            locationCity: org.locationCity,
          },
        });
      }
    } catch (err) {
      console.error("Failed to save organization:", err);
    }
  };

  // Save all
  const handleSave = async () => {
    setisLoading(true);
    for (let org of organizations) {
      await saveOrganization(org);
    }
    setisEdit(false);
    loadOrgData();
    setisLoading(false)
    setIsFading(true);
    setTimeout(() => {
      if ((currentTabIndex + 1) > profileCompletion) {
        updatePercentage(currentTabIndex + 1)
      }
      setCurrentTabIndex(currentTabIndex + 1);
      setIsFading(false);
    }, 200);
  };

  // Delete
  const handleDelete = async (index, org) => {
    try {
      if (org.organizationHistoryID) {
        await deleteData({ url: "OrganizationalHistory", id: org.organizationHistoryID, userToken });
      }
      setOrganizations(organizations.filter((_, i) => i !== index));
    } catch (err) {
      console.error("Failed to delete organization:", err);
    }
  };

  return (
    <div>

      <div className="p-6 mt-4 bg-white rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.05)] mx-auto border" style={{ minHeight: window.innerHeight - 300 }}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-blue-700 font-medium md:text-[20px] text-[17px] flex items-center space-x-2 cursor-pointer">
              <img src={"/asset/icon/organization_blue.svg"} className="w-[16px] mr-1" />
              <span>{Lang(lang, 'organization')}</span>
            </h2>
            <p className="text-[11px] md:text-[14px] font-normal text-[#1E3A8A]">{Lang(lang, 'org_subtitle')}</p>
          </div>
          {!isEdit && (
            <button
              onClick={() => setisEdit(true)}
              className="flex border border-gray-300 rounded-full px-5 py-2 text-sm text-white bg-red-500 hover:bg-red-600"
            >
              <img src={"/asset/icon/edit.svg"} className="w-[16px] mr-1" />
              Edit
            </button>
          )}
        </div>

        {organizations.length === 0 ? <div className="w-full">
          <EmptyAsset title={Lang(lang, 'no_organization')} desc={Lang(lang, 'no_organization_desc')} />
          {isEdit && <button
            type="button"
            onClick={handleAdd}
            className="w-full text-blue-600 border border-dashed border-blue-300 rounded-full py-2 text-sm hover:bg-blue-50 mb-4"
          >
            + {Lang(lang, 'add_org')}
          </button>}
        </div> : organizations.map((org, index) => (
          <div key={index} className="border rounded-lg p-4 mb-4 bg-gray-50 relative text-sm">
            {isEdit && (
              <button
                type="button"
                onClick={() => handleDelete(index, org)}
                className="absolute top-3 right-3 text-red-500 hover:text-red-700"
                title="Delete"
              >
                <img src={"/asset/icon/trash.svg"} className="w-[16px]" />
              </button>
            )}

            <h3 className="text-sm font-bold text-blue-700 mb-2">
              {Lang(lang, 'organization')} {index + 1}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">{Lang(lang, 'organization')}<span className="text-red-500">*</span></label>
                <input
                  disabled={!isEdit}
                  type="text"
                  value={org.organizationName || ""}
                  onChange={(e) => handleChange(index, "organizationName", e.target.value)}
                  className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                  placeholder={Lang(lang, 'organization')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{Lang(lang, 'position')}<span className="text-red-500">*</span></label>
                <input
                  disabled={!isEdit}
                  type="text"
                  value={org.position || ""}
                  onChange={(e) => handleChange(index, "position", e.target.value)}
                  className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                  placeholder={Lang(lang, 'position')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{Lang(lang, 'year')}<span className="text-red-500">*</span></label>
                <input
                  disabled={!isEdit}
                  type="number"
                  value={org.year || ""}
                  onChange={(e) => handleChange(index, "year", e.target.value)}
                  className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                  placeholder={Lang(lang, 'year')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{Lang(lang, 'location')}<span className="text-red-500">*</span></label>
                <input
                  disabled={!isEdit}
                  type="text"
                  value={org.locationCity || ""}
                  onChange={(e) => handleChange(index, "locationCity", e.target.value)}
                  className={(isEdit ? 'bg-white' : 'bg-gray-100') + " w-full rounded-full border border-gray-200 px-4 py-2 h-[60px]"}
                  placeholder="City"
                />
              </div>
            </div>
          </div>
        ))}

        {isEdit && organizations.length !== 0 && (
          <button
            type="button"
            onClick={handleAdd}
            className="w-full text-blue-600 border border-dashed border-blue-300 rounded-full py-2 text-sm hover:bg-blue-50 mb-4"
          >
            + {Lang(lang, 'add_org')}
          </button>
        )}


        {!(isFormReady()) && <div className="flex justify-end w-full">
          <p className="text-red-500 text-xs mt-1 mr-0">{Lang(lang, 'required_error')}</p>
        </div>}
      </div>

      <div className="flex space-x-2 mt-3 justify-end w-full bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] p-3 rounded-xl sticky bottom-3">
        <button
          type="button"
          className="border border-gray-300 rounded-full px-4 py-1 text-sm hover:bg-gray-50"
          onClick={() => {
            setIsFading(true);
            setTimeout(() => {
              setCurrentTabIndex(currentTabIndex - 1);
              setIsFading(false);
            }, 200);
          }}
        >
          {Lang(lang, 'previous')}
        </button>
        <button
          type="button"
          className={
            "rounded-full px-4 py-1 text-sm " +
            (isEdit && isFormReady() ? "text-white bg-red-500 hover:bg-red-600" : "border border-red-500 text-red-500 hover:bg-gray-50")
          }
          onClick={async () => {
            if (isFormReady()) {
              if (isEdit && !isLoading) {
                await handleSave();
              } else {
                setIsFading(true);
                setTimeout(() => {
                  if ((currentTabIndex + 1) > profileCompletion) {
                    updatePercentage(currentTabIndex + 1)
                  }
                  setCurrentTabIndex(currentTabIndex + 1);
                  setIsFading(false);
                }, 200);
              }
            }
          }}
        >
          {isLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /> : isEdit ? Lang(lang, 'save_and_next') : `${Lang(lang, 'next')} ${currentTabIndex + 1}/10`}
        </button>
      </div>

    </div>
  );
};

export default OrganizationForm;
